import { api } from '../console/api.ts';
import { empty, field, pageHead, pill } from '../console/components.ts';
import { ApiError, InputError } from '../console/errors.ts';
import { esc, icon, ROLE } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { administersNubi } from '../console/session.ts';
import { $, act, textInput } from '../console/ui.ts';
import { REQUIRED_HINT, selectControl, textControl } from '../controls.ts';

/**
 * «Administración de Nubi › Administradores» (#274, ADR-0026): the people of Nubi's own
 * organization with their role (administración or operaciones) and status (active once they
 * signed in, invited until then). The administration invites a person by e-mail, changes a
 * member's role and removes a member's access, each confirmed on this screen (never a browser
 * dialog). The API keeps Nubi administered: the last active administrator is never demoted nor
 * removed, and nobody removes themselves; the screen says why when it refuses.
 */

export type NubiPerson = {
  memberId: string;
  email: string;
  name: string;
  role: 'nubi_admin' | 'nubi_ops' | null;
  status: 'ACTIVE' | 'INVITED' | string;
  self: boolean;
};

const STATUS: Record<string, [state: string, label: string]> = {
  ACTIVE: ['ok', 'Activa'],
  INVITED: ['warn', 'Invitada'],
};
/** What each role of Nubi does, in the person's words. */
const ROLE_HELP: Record<string, string> = {
  nubi_admin: 'Configura las agencias, las personas de Nubi y la plataforma.',
  nubi_ops: 'Operaciones de Nubi: no ve la administración ni los datos de las agencias.',
};
const roleName = (r: string | null) => (r ? (ROLE[r] ?? r) : 'Sin rol de Nubi');

/** What the screen shows above the list: the invitation, a confirmation, or why it refused. */
type Panel =
  | { kind: 'invite' }
  | { kind: 'role'; memberId: string; role: 'nubi_admin' | 'nubi_ops' }
  | { kind: 'remove'; memberId: string }
  | { kind: 'refused'; title: string; text: string };
let panel: Panel | null = null;
/** The people of the last paint (the confirmations read them). */
let shown: NubiPerson[] = [];

/** Back to the plain list (a new session, or the menu). */
export function resetAdministradores() {
  panel = null;
  shown = [];
}

const personOf = (id: string) => shown.find((p) => p.memberId === id);
const activeAdmins = () =>
  shown.filter((p) => p.role === 'nubi_admin' && p.status === 'ACTIVE').length;
/** Whether taking this person's administration would leave Nubi without one (the API decides). */
const isLastAdmin = (p: NubiPerson) =>
  p.role === 'nubi_admin' && p.status === 'ACTIVE' && activeAdmins() <= 1;

const LAST_ADMIN_TEXT =
  'Nubi necesita al menos una persona administradora activa. Invita a otra persona como administradora y espera a que entre a Nubi; después podrás cambiar este rol o quitar este acceso.';

function inviteHtml() {
  return `<form id="admin-invite-form" class="card" data-component="admin-invite" novalidate>
    <div class="card-head"><h2>Invitar a una persona de Nubi</h2></div>
    ${REQUIRED_HINT}
    <div class="form-grid"><fieldset><legend>La persona</legend>
      ${field('Nombre', textControl({ id: 'admin-name', maxlength: 120, autocomplete: 'name' }), { required: true })}
      ${field('Correo', textControl({ id: 'admin-email', type: 'email', maxlength: 254, autocomplete: 'email', hint: 'Le enviamos la invitación para entrar.' }), { required: true })}
      ${field(
        'Rol',
        selectControl({ id: 'admin-role', label: 'Rol' }, [
          { value: 'nubi_ops', label: ROLE.nubi_ops ?? 'nubi_ops', selected: true },
          { value: 'nubi_admin', label: ROLE.nubi_admin ?? 'nubi_admin' },
        ]),
        { required: true },
      )}
    </fieldset></div>
    <p class="note info">${icon('shield')}<span>${esc(ROLE_HELP.nubi_admin)} ${esc(ROLE_HELP.nubi_ops)}</span></p>
    <div class="form-foot"><span class="muted small">Stytch le envía el correo; aparece como «Invitada» hasta que entre.</span>
      <span class="spacer"></span>
      <button type="button" class="btn" data-admin-cancel>Cancelar</button>
      <button type="submit" class="btn primary" id="admin-invite-submit">Invitar</button></div>
  </form>`;
}

/** A confirmation that says the consequence and waits for a second, deliberate click. */
function confirmHtml(p: NubiPerson, change: Extract<Panel, { kind: 'role' | 'remove' }>) {
  const removing = change.kind === 'remove';
  const title = removing
    ? `¿Quitar el acceso de ${p.name}?`
    : `¿Cambiar el rol de ${p.name} a ${roleName(change.role)}?`;
  const last = removing
    ? isLastAdmin(p)
    : change.kind === 'role' && change.role !== 'nubi_admin' && isLastAdmin(p);
  const text = last
    ? LAST_ADMIN_TEXT
    : removing
      ? `${p.email} ya no podrá entrar a Nubi. La bitácora guarda quién le quitó el acceso y cuándo.`
      : `${ROLE_HELP[change.kind === 'role' ? change.role : ''] ?? ''}${p.self ? ' Es tu propio rol: lo verás al volver a entrar.' : ''}`;
  const tone = last || removing ? 'bad' : 'info';
  const button = removing
    ? `<button type="button" class="btn danger" data-admin-remove-confirm="${esc(p.memberId)}">Sí, quitar el acceso</button>`
    : `<button type="button" class="btn primary" data-admin-role-confirm="${esc(p.memberId)}" data-role="${esc(change.kind === 'role' ? change.role : '')}">Sí, cambiar el rol</button>`;
  return `<section class="card" role="alertdialog" aria-modal="false" aria-labelledby="admin-confirm-title" aria-describedby="admin-confirm-text" data-component="admin-confirm">
    <div class="card-head"><h2 id="admin-confirm-title">${esc(title)}</h2></div>
    <p class="note ${tone}" id="admin-confirm-text">${icon(last || removing ? 'alert' : 'ok')}<span>${esc(text)}</span></p>
    <div class="form-foot"><span class="spacer"></span>
      <button type="button" class="btn" data-admin-cancel>${last ? 'Entendido' : 'Cancelar'}</button>
      ${last ? '' : button}</div>
  </section>`;
}

/** Why the API refused (the last administrator, one's own access), on screen until closed. */
const refusedHtml = (title: string, text: string) =>
  `<section class="card" role="alert" data-component="admin-refused">
    <div class="card-head"><h2 id="admin-refused-title">${esc(title)}</h2></div>
    <p class="note bad">${icon('alert')}<span>${esc(text)}</span></p>
    <div class="form-foot"><span class="spacer"></span><button type="button" class="btn" data-admin-cancel>Entendido</button></div>
  </section>`;

export async function administradores() {
  const description =
    'Las personas de la organización Nubi: su rol, si ya entraron, y quién puede administrar la plataforma.';
  if (!administersNubi())
    return `${pageHead('Administradores', description)}${empty('lock', 'Esta sección es de la administración de Nubi', 'Tu rol no administra a las personas de Nubi.', '', 2)}`;
  const inviteButton = `<button type="button" class="btn primary" data-admin-invite>${icon('users')}Invitar persona</button>`;
  const head = pageHead(
    'Administradores',
    description,
    panel?.kind === 'invite' ? '' : inviteButton,
  );
  const people = await api<NubiPerson[]>('GET', '/v1/platform/admins');
  shown = people;
  const p = panel;
  const target = p && (p.kind === 'role' || p.kind === 'remove') ? personOf(p.memberId) : undefined;
  const top =
    p?.kind === 'invite'
      ? inviteHtml()
      : p?.kind === 'refused'
        ? refusedHtml(p.title, p.text)
        : target && p && (p.kind === 'role' || p.kind === 'remove')
          ? confirmHtml(target, p)
          : '';
  if (people.length === 0)
    return `${head}${top}${empty('users', 'Nubi todavía no tiene personas', 'Invita a la primera con «Invitar persona».', '', 2)}`;
  const rows = people
    .map((x) => {
      const [state, label] = STATUS[x.status] ?? ['warn', x.status];
      const other = x.role === 'nubi_admin' ? 'nubi_ops' : 'nubi_admin';
      const buttons = [
        `<button type="button" class="btn sm" data-admin-role="${esc(x.memberId)}" data-role="${other}" aria-label="Cambiar el rol de ${esc(x.name)} a ${esc(roleName(other))}">${other === 'nubi_admin' ? 'Hacer administradora' : 'Pasar a operaciones'}</button>`,
        x.self
          ? ''
          : `<button type="button" class="btn sm danger" data-admin-remove="${esc(x.memberId)}" aria-label="Quitar el acceso de ${esc(x.name)}">Quitar acceso</button>`,
      ].filter(Boolean);
      return `<tr data-admin="${esc(x.memberId)}"><td><strong>${esc(x.name)}</strong>${x.self ? ' <span class="muted small">(tú)</span>' : ''} <br><span class="muted small">${esc(x.email)}</span></td>
        <td>${esc(roleName(x.role))}</td><td>${pill(state, label)}</td>
        <td class="actions"><span class="row-actions">${buttons.join(' ')}</span></td></tr>`;
    })
    .join('');
  const admins = activeAdmins();
  return `${head}${top}<section class="card" data-component="platform-admins"><div class="card-head"><h2>Personas de Nubi</h2><span class="spacer"></span><span class="muted small">${admins} ${admins === 1 ? 'administradora activa' : 'administradoras activas'} de ${people.length}</span></div>
    <div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Personas de la organización Nubi</caption>
    <thead><tr><th scope="col">Persona</th><th scope="col">Rol</th><th scope="col">Estado</th><th scope="col" class="actions">Acciones</th></tr></thead>
    <tbody>${rows}</tbody></table></div></section>`;
}

/** Paints the screen again and puts the focus where the person continues. */
async function repaint(focus?: string) {
  await render(current, { quiet: true });
  if (focus) document.querySelector<HTMLElement>(focus)?.focus();
}
const open = (p: Panel | null, focus?: string) => {
  panel = p;
  void repaint(focus);
};

/**
 * The API's refusals that keep Nubi administered, shown on the screen (not only in a toast):
 * the panel says why, and the toast too.
 */
async function guarded(run: () => Promise<string>): Promise<string> {
  try {
    return await run();
  } catch (e) {
    if (e instanceof ApiError && (e.code === 'LAST_ADMIN' || e.code === 'CANNOT_REMOVE_SELF')) {
      const self = e.code === 'CANNOT_REMOVE_SELF';
      panel = {
        kind: 'refused',
        title: self ? 'No puedes quitarte tu propio acceso' : 'Nubi se quedaría sin administración',
        text: self
          ? 'Pide a otra persona administradora que lo haga, si de verdad debes dejar Nubi.'
          : LAST_ADMIN_TEXT,
      };
      await repaint('#admin-refused-title');
    }
    // Stytch did not answer: nothing changed and nothing was written.
    if (e instanceof ApiError && e.code === 'IDENTITY_UNAVAILABLE')
      throw new InputError(
        'Stytch no respondió: no se cambió nada. Intenta de nuevo en un momento.',
      );
    throw e;
  }
}

const value = (id: string) => ($(`#${id}`) as HTMLInputElement | null)?.value?.trim() ?? '';

export const actions: Actions = {
  click: {
    adminInvite: () => open({ kind: 'invite' }, '#admin-name'),
    adminCancel: () => open(null, '[data-admin-invite]'),
    adminRole: (_b, d) => {
      const role = d.role === 'nubi_admin' ? 'nubi_admin' : 'nubi_ops';
      open({ kind: 'role', memberId: d.adminRole ?? '', role }, '#admin-confirm-title');
    },
    adminRemove: (_b, d) =>
      open({ kind: 'remove', memberId: d.adminRemove ?? '' }, '#admin-confirm-title'),
    adminRoleConfirm: (b, d) => {
      const p = personOf(d.adminRoleConfirm ?? '');
      const role = d.role === 'nubi_admin' ? 'nubi_admin' : 'nubi_ops';
      if (!p) return;
      void act(b, 'Cambiando el rol…', () =>
        guarded(async () => {
          await api('PUT', `/v1/platform/admins/${encodeURIComponent(p.memberId)}`, { role });
          panel = null;
          await repaint();
          return `${p.name} ahora es ${roleName(role)}.`;
        }),
      );
    },
    adminRemoveConfirm: (b, d) => {
      const p = personOf(d.adminRemoveConfirm ?? '');
      if (!p) return;
      void act(b, 'Quitando el acceso…', () =>
        guarded(async () => {
          await api('DELETE', `/v1/platform/admins/${encodeURIComponent(p.memberId)}`);
          panel = null;
          await repaint();
          return `${p.name} ya no tiene acceso a Nubi.`;
        }),
      );
    },
  },
  submit: {
    'admin-invite-form': (ev) => {
      ev.preventDefault();
      const form = ev.target as HTMLFormElement;
      void act(form.querySelector<HTMLElement>('[type="submit"]'), 'Invitando…', () =>
        guarded(async () => {
          const name = textInput('#admin-name', 'el nombre');
          const email = textInput('#admin-email', 'el correo').toLowerCase();
          if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))
            throw new InputError('Escribe un correo válido.', 'admin-email');
          const role = value('admin-role') === 'nubi_admin' ? 'nubi_admin' : 'nubi_ops';
          const out = await api<{ invited: boolean }>('POST', '/v1/platform/admins', {
            name,
            email,
            role,
          });
          panel = null;
          await repaint();
          return out.invited
            ? `Invitamos a ${email} como ${roleName(role)}.`
            : `${email} ya es parte de Nubi: no se envió otra invitación.`;
        }),
      );
    },
  },
};
